import React from 'react';
import { Plus } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ApiUrls } from '@api/apiUrls';
import { IFormField } from '@components-shared/forms/formField.interface';
import { TableProps } from '@components-shared/table/types';
import { keyMap } from '@locales/lokalise/keyMap';
import { ENUM_CONSTANTS } from 'components/shared/enum.constants';
import { ExtractUnionName } from 'components/shared/enum.types';
import { FormattedDateTime } from 'components/shared/formattedDateTime';
import Table from 'components/shared/table/index';
import { IAccountUser } from 'global.interfaces';
import { getValueFromEnums, getTableLabel, getLink, onDownload } from 'utils/helper';
import { IReportRow } from '../types';

interface IPreviousReportProps {
  api?: string;
  downloadApi?: string;
  rootPath?: string;
  truncatePagination?: boolean;
  isUser?: boolean;
  componentId?: number;
}

type CtxProps = { props: IPreviousReportProps };

const COLUMNS = [
  {
    key: 'id',
    name: keyMap.sharedFieldsId,
    render: (row: IReportRow, value: string) => getLink(value, `/admin/reports/${value}`),
    isHidden: (ctx: CtxProps) => ctx.props.isUser === true,
  },
  {
    key: 'title',
    name: keyMap.partnershipIbInfoStatisticsPreviousReportDocTitle,
  },
  {
    key: 'country',
    name: keyMap.sharedFieldsCountry,
    render: (row: IReportRow, value: string) => getValueFromEnums(value, ENUM_CONSTANTS.COUNTRY),
    isHidden: (ctx: CtxProps) => ctx.props.isUser === true,
  },
  {
    key: 'type',
    name: keyMap.sharedFieldsType,
    render: (row: IReportRow, value: string) => getValueFromEnums(value, ENUM_CONSTANTS.REPORT_TYPE),
  },
  {
    key: 'status',
    name: keyMap.commonStatus,
    render: (row: IReportRow, value: string) => getTableLabel(value, ENUM_CONSTANTS.REPORT_STATUS),
    isHidden: (ctx: CtxProps) => ctx.props.isUser === true,
  },
  {
    key: 'user',
    name: keyMap.sharedFieldsUser,
    render: (row: IReportRow, value: IAccountUser) =>
      row.user.id ? getLink(value.fullName ? value.fullName : value.email, `/admin/users/${row.user.id}`) : '',
    isHidden: (ctx: CtxProps) => ctx.props.isUser === true,
  },
  {
    key: 'createdOn',
    name: keyMap.sharedFieldsCreatedOn,
    render: (row: IReportRow, value: string) => {
      return <FormattedDateTime value={value} />;
    },
  },
];

const USER_REPORT_NAMES: ExtractUnionName<typeof ENUM_CONSTANTS.REPORT_TYPE>[] = [
  'enum.reportType.closedTrades',
  'enum.reportType.tradingVolume',
];

const getFields = (isUser: boolean | undefined): IFormField[] => {
  let reportTypes = ENUM_CONSTANTS.REPORT_TYPE.sort((a, b) => {
    return a.name > b.name ? 1 : -1;
  });

  if (isUser === true) {
    reportTypes = reportTypes.filter((t) => USER_REPORT_NAMES.includes(t.name));
  }

  return [
    {
      name: 'createdOn',
      title: keyMap.sharedFieldsCreatedOn,
      type: 'dateTimeRange',
      timeFormat: false,
    },
    {
      name: 'user',
      title: keyMap.sharedFieldsUser,
      isHidden: (ctx: CtxProps) => ctx.props.isUser === true,
    },
    {
      name: 'type',
      title: keyMap.sharedFieldsType,
      type: 'select',
      options: reportTypes,
    },
    {
      name: 'status',
      title: keyMap.commonStatus,
      type: 'select',
      options: ENUM_CONSTANTS.REPORT_STATUS,
      isHidden: (ctx: CtxProps) => ctx.props.isUser === true,
    },
    {
      name: 'country',
      title: keyMap.sharedFieldsCountry,
      type: 'select',
      options: ENUM_CONSTANTS.COUNTRY,
      isHidden: (ctx: CtxProps) => ctx.props.isUser === true,
    },
    {
      name: 'componentId',
      type: 'hidden',
      defaultValue: (ctx: CtxProps) => ctx.props.componentId || null,
    },
  ];
};

const ReportGrid: React.FC<IPreviousReportProps> = (props) => {
  const {
    api = ApiUrls.reportAdmin.grid,
    downloadApi = ApiUrls.reportAdmin.download,
    isUser,
    truncatePagination = false,
  } = props;

  const actions = [
    {
      name: keyMap.sharedActionsDetails,
      type: 'link',
      link: '/admin/reports',
      isHidden: () => !!isUser,
    },
    {
      name: keyMap.sharedActionsDownload,
      onClick: (data: IReportRow) => onDownload(`${downloadApi}?id=${data.id}`),
      isHidden: (data: IReportRow) => !data.fileItem.fileName,
    },
  ];

  const fields = getFields(isUser);

  return (
    <Table
      tableId="previous-report"
      columns={COLUMNS as TableProps['columns']}
      filterFields={fields as TableProps['filterFields']}
      actions={actions as unknown as TableProps['actions']}
      apiExplicitRequest={true}
      api={api}
      parentContext={{ props }}
      truncatePagination={truncatePagination}
    />
  );
};

export const PreviousReport = (props: IPreviousReportProps): JSX.Element => {
  const { i18n } = useTranslation();
  const { componentId, isUser, rootPath } = props;
  const isComponent = !!componentId;

  if (isUser || isComponent) {
    return <ReportGrid {...props} />;
  }

  return (
    <div className="content">
      <div className="content__header">
        <div className="content__header-right">
          <div className="btn-group">
            <Link className="btn-group__btn" to={`${rootPath}/create`}>
              <Plus size={13} className="btn-group__btn-ico" />
              {i18n.t(keyMap.sharedActionsCreate)}
            </Link>
          </div>
        </div>
        <h1 className="content__title">Reports History</h1>
      </div>

      <div className="panel">
        <ReportGrid {...props} />
      </div>
    </div>
  );
};
